/* TypeInfoEditor.js */

"use strict";

const { getTypeData } = require("../CustomFieldsConstants");

class TypeInfoEditor {
    /**
     * Editor lifecycle:
     * construct -> loadValue(itemData) -> serializeValue -> isValueChanged -> validate ->
     *  -> serializeValue -> applyValue -> loadValue -> destroy
     *
     * @typedef {CustomFieldData} ItemData - Slick Grid data item
     * @typedef {{top: number,
     *           left: number,
     *           bottom: number,
     *           right: number,
     *           width: number,
     *           height: number,
     *           visible: boolean}} SlickPosition - object describing cell position
     *
     *
     * @param {Object}          options                 - initial editor options
     * @param {Object}          options.column          - Slick Grid column definition data
     * @param {Object}          options.container       - jQuery Slick Grid Cell object
     * @param {SlickGrid}       options.grid            - SlickGrid instance
     * @param {SlickPosition}   options.gridPosition    - Object describing cell position
     * @param {ItemData}        options.item            - Slick Grid data item
     * @param {SlickPosition}   options.position        - Abs SlickGrid grid cell position
     * @param {Function}        options.cancelChanges   - grid method to discard changes
     * @param {Function}        options.commitChanges   - grid method to store changes
     */
    constructor(options) {
        const columnData = options.column;
        const itemData = options.item;

        this._isNewItem = Boolean(itemData.isNew);
        this._fieldName = columnData.field;
        this._$container = document.body;//options.grid.getContainerNode().parentNode; //options.container;
        this._$container.style.overflow = "hidden";

        this.typeData = getTypeData(itemData.dataType) || {};

        this.editorContainer = document.createElement("div");
        this.editorContainer.component = this;
        Object.assign(this.editorContainer.style, {
            minWidth: options.position.width + "px",
            minHeight: options.position.height + "px"
        });
        this.position(options.position);

        // eslint-disable-next-line no-use-before-define
        this.EditorComponent = this.typeData.editor || NoopClass;
        this._init(options);
    }

    _init(options) {
        this.editorComponent = new this.EditorComponent(options, this._isNewItem);
        if (this.editorComponent.isNoop) {
            return;
        }

        this.editorContainer.classList.add("typeInfo-editor", "editor-wrapper");
        this.editorContainer.append(this.editorComponent.rootElement);
        this._$container.append(this.editorContainer);

        this._registerListeners();
    }

    _registerListeners() {
        this.editorContainer.addEventListener(
            "keydown",
            (this._onRootKeydown = this._onRootKeydown.bind(this))
        );
    }

    _unregisterListeners() {
        this.editorContainer.removeEventListener("keydown", this._onRootKeydown);
    }

    _onRootKeydown(e) {
        if (e.key.toLowerCase().match("arrow") && e.target.tagName === "INPUT") {
            // do not lose editor on arrow navigation in inputs
            e.stopPropagation();
        }
    }

    destroy() {
        this._unregisterListeners();
        this.editorContainer.remove();
        this._$container.style.removeProperty("overflow");

        this.editorComponent.destroy();
        this.editorContainer = null;
    }

    show() {
        this.editorContainer.style.removeProperty("display");
    }

    hide() {
        this.editorContainer.style.setProperty("display", "none");
    }

    position ({top, left}) {
        Object.assign(this.editorContainer.style, {
            top: `${top}px`,
            left: `${left}px`,
        });
    }

    focus() {
        this.editorComponent.focus();
    }

    /**
     * is used to load value from item Data and set it to 'input'
     * @param {ItemData} item
     */
    loadValue(item) {
        const fieldValue = item[this._fieldName];
        this.editorComponent.setData(fieldValue);
    }

    /**
     * is used to get data from 'input'
     * @returns {*}
     */
    serializeValue() {
        return this.editorComponent.getData();
    }

    /**
     * is used to apply new data back to Item
     * @param {ItemData} item - grid data Item
     * @param {*} state - result of serializeValue
     */
    applyValue(item, state) {
        item[this._fieldName] = state;

        //validate current default value according to new type info;
        const validity =
            this.typeData.validation && this.typeData.validation(item.default, item.typeInfo);

        if (validity && validity.valid === false) {
            item["default"] = this.typeData.default; //just in case if current default become invalid
        }
    }

    /**
     * Compares initial and edited data.
     * If false the destroy will be called the next.
     * If true validate will be called the next.
     * @returns {boolean}
     */
    isValueChanged() {
        return this.editorComponent.isValueChanged();
    }

    /**
     * checks if data is valid
     *
     * @returns {{valid: boolean, msg: ?string}}
     */

    validate() {
        return this.editorComponent.validate();
    }
}

module.exports = TypeInfoEditor;

/* jshint -W003 */
class NoopClass {
    constructor(options) {
        this.isNoop = true;
        setTimeout(options.cancelChanges);
    }

    get rootElement() {
        return document.createElement("span");
    }

    init() {}

    destroy() {}

    setData() {}

    getData() {}

    isValueChanged() {
        return false;
    }

    validate() {
        return {
            valid: true,
            msg: null
        };
    }

    focus() {}
}

/* jshint +W003 */
